import { ArrowLeft, Edit, LucideIcon } from 'lucide-react';
import React, { useState } from 'react';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Circle, MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { useLocation, useNavigate } from 'react-router-dom';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface Stat {
  label: string;
  value: number;
  icon: LucideIcon;
  bgColor: string;
  iconColor: string;
  countColor: string;
}

interface Detail {
  id: string;
  label: string;
  value: string;
  required?: boolean;
  type: 'text' | 'select';
  options?: string[];
  icon: LucideIcon;
  half?: boolean;
}

type UpdateDetailFn = (id: string, newValue: string) => void;

const EditProjectSite: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { project, site } = (location.state as any) || {};
  const [siteData, setSiteData] = useState<any>(() => {
    if (site) return site;
    if (project) {
      return {
        siteCode: `${project.id}-${Date.now()}`,
        clientName: project.projectName,
        projectId: project.id,
      };
    }
    return null;
  });

  const [siteDetails, setSiteDetails] = useState<Detail[]>(siteData.details);
  const [tempDetails, setTempDetails] = useState<Detail[]>(siteData.details);
  const [status, setStatus] = useState<string>(siteData.status || 'Active');
  const [tempStatus, setTempStatus] = useState<string>(siteData.status || 'Active');
  const [isEditing, setIsEditing] = useState(false);

  const updateDetail: UpdateDetailFn = (id, newValue) => {
    let processedValue = newValue;
    if ((id === 'radius' || id === 'radiusMeters') && newValue !== '') {
      processedValue = Math.max(1, Number(newValue)).toString();
    }
    if (id === 'maxUsers' && newValue !== '') {
      processedValue = Math.max(0, Number(newValue)).toString();
    }
    if ((id === 'radius' || id === 'radiusMeters' || id === 'maxUsers') && newValue === '') {
      processedValue = '';
    }
    setTempDetails((prev) => prev.map((d) => (d.id === id ? { ...d, value: processedValue } : d)));
  };

  const handleSave = async () => {
    // Validate Site Name
    const siteNameDetail = tempDetails.find((d) => d.id === 'siteName');
    if (siteNameDetail) {
      const siteName = siteNameDetail.value.trim();
      if (!siteName) {
        alert('Site name is required');
        return;
      }
      if (siteName.length < 5 || siteName.length > 60) {
        alert('Site name must be 5–60 characters.');
        return;
      }
    }

    const updatedSiteData = {
      ...siteData,
      details: tempDetails,
      status: tempStatus,
    };

    setSiteData(updatedSiteData);
    setSiteDetails(tempDetails);
    setStatus(tempStatus);
    setIsEditing(false);
  };

  const handleDiscard = () => {
    setTempDetails(siteDetails);
    setTempStatus(status);
    setIsEditing(false);
  };

  const activeColor = status === 'Active' ? 'bg-green-500' : 'bg-red-500';

  const lat = parseFloat(siteData.lat) || 17.4065;
  const lng = parseFloat(siteData.lng) || 78.4772;
  const radius = siteData.radius || siteData.radiusMeters || null;

  return (
    <div className="min-h-screen p-4 sm:p-8 bg-pink-50 font-inter text-gray-800">
      {/* Header */}
      <header className="flex justify-between items-start mb-6 border-b border-[#4A082D] pb-4">
        <div>
          <h1 className="text-3xl font-semibold">Project Management</h1>
          <p className="text-gray-500 mt-1">Manage all projects you have access to</p>
        </div>
        <button
          className="flex items-center text-sm text-fuchsia-600 hover:text-fuchsia-800 transition duration-150"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Return to project dashboard
        </button>
      </header>

      {/* Main Content */}
      <div className="bg-pink-100 p-4 sm:p-6 rounded-2xl shadow-xl">
        {/* Client Info */}
        <div className="flex justify-between items-center p-2 border-b bg-pink-50 rounded-lg border-gray-100">
          <div className="flex items-center space-x-4">
            <div className="border bg-gray-300 rounded-lg">
              <img
                src={project?.image || siteData.image || '/default-project.png'}
                alt="logo"
                className="w-10 h-10 rounded-full"
              />
            </div>
            <div>
              <p className="text-lg font-bold tracking-tight">{siteData.siteCode}</p>
              <h2 className="text-2xl font-bold text-gray-900 leading-none">
                {project?.projectName || siteData.clientName}
              </h2>
            </div>
          </div>
          <button
            title="Maximize View"
            className="p-2 text-gray-400 hover:text-fuchsia-500 transition duration-150 rounded-full hover:bg-fuchsia-50"
          >
            <img
              src="/Vector.png"
              alt="Expand Map"
              className="w-5 h-5 transform transition-transform duration-300"
            />
          </button>
        </div>

        {/* Stats */}
        <section className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
          {siteData.stats.map((stat: Stat, index: number) => (
            <div
              key={index}
              className="flex flex-col p-4 bg-pink-50 rounded-xl shadow-sm hover:shadow-md transition"
            >
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-sm text-gray-500">{stat.label}</span>
                  <div className={`text-3xl font-bold ${stat.countColor}`}>{stat.value}</div>
                </div>
                <div className={`p-3 rounded-xl ${stat.bgColor} ${stat.iconColor}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Site Details */}
        <div className="mt-8">
          <h3 className="text-md font-semibold mb-4 text-[#4A082D] border-b pb-2">
            {siteData.siteCode || 'Site'} (required)
          </h3>

          <div className="bg-pink-100 p-4 rounded shadow-inner">
            <div className="flex flex-wrap -mb-2">
              {(isEditing ? tempDetails : siteDetails).map((detail) => {
                const commonClasses =
                  'w-full p-3 border border-fuchsia-200 rounded-lg bg-[#4A082D]/10 text-sm text-gray-800 focus:outline-none';
                const labelClass = 'block text-sm font-semibold text-gray-700 mb-1';
                const isDisabled = !isEditing;

                if (detail.type === 'select') {
                  return (
                    <div key={detail.id} className="w-full px-2 mb-3">
                      <label className={labelClass}>{detail.label}</label>
                      <select
                        className={`${commonClasses} ${
                          isDisabled ? 'cursor-not-allowed bg-opacity-60' : 'bg-[#4A082D]/10'
                        }`}
                        value={detail.value}
                        onChange={(e) => updateDetail(detail.id, e.target.value)}
                        disabled={isDisabled}
                      >
                        {detail.options?.map((opt) => (
                          <option key={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  );
                }

                return (
                  <div
                    key={detail.id}
                    className={`${detail.half ? 'w-full md:w-1/2' : 'w-full'} px-2 mb-3`}
                  >
                    <label className={labelClass}>{detail.label}</label>
                    <input
                      type={
                        detail.id === 'radius' ||
                        detail.id === 'radiusMeters' ||
                        detail.id === 'lat' ||
                        detail.id === 'lng' ||
                        detail.id === 'maxUsers'
                          ? 'number'
                          : 'text'
                      }
                      min={
                        detail.id === 'radius' || detail.id === 'radiusMeters'
                          ? '1'
                          : detail.id === 'maxUsers'
                            ? '0'
                            : undefined
                      }
                      className={`${commonClasses} ${
                        isDisabled ? 'cursor-not-allowed bg-opacity-60' : 'bg-[#4A082D]/10'
                      }`}
                      value={
                        (detail.id === 'maxUsers' ||
                          detail.id === 'radius' ||
                          detail.id === 'radiusMeters') &&
                        detail.value === '0'
                          ? ''
                          : detail.value || ''
                      }
                      placeholder={
                        detail.id === 'radius' || detail.id === 'radiusMeters'
                          ? 'Radius (in meters)'
                          : detail.id === 'lat'
                            ? 'Latitude'
                            : detail.id === 'lng'
                              ? 'Longitude'
                              : detail.id === 'maxUsers'
                                ? 'Max Users'
                                : undefined
                      }
                      readOnly={isDisabled}
                      onChange={(e) => updateDetail(detail.id, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>

            {/* Status */}
            <div className="flex items-center p-3 rounded-lg bg-[#4A082D]/10 mt-4 mb-2 justify-between">
              <div className="flex items-center">
                <span className="text-xs font-semibold text-gray-700 mr-4">Status -</span>

                {isEditing ? (
                  <select
                    value={tempStatus}
                    onChange={(e) => setTempStatus(e.target.value)}
                    className="px-3 py-1 text-sm font-semibold rounded-full bg-[#4A082D]/10 border border-fuchsia-200 focus:outline-none"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                ) : (
                  <span
                    className={`px-3 py-1 text-sm font-semibold rounded-full text-white ${activeColor}`}
                  >
                    {status}
                  </span>
                )}
              </div>
            </div>

            {/* Buttons */}
            {isEditing && (
              <div className="flex justify-end gap-4 mt-4">
                <button
                  onClick={handleDiscard}
                  className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Discard
                </button>
                <button
                  onClick={handleSave}
                  className="px-5 py-2 bg-[#0BA96F] text-white rounded-lg hover:bg-[#0BA96F]/70 transition font-medium"
                >
                  Save
                </button>
              </div>
            )}
          </div>

          {/* Map */}
          <div className="relative mt-6 h-64 sm:h-80 w-full rounded-xl overflow-hidden shadow-lg border border-gray-200">
            <MapContainer
              center={[lat, lng]}
              zoom={6}
              scrollWheelZoom={true}
              maxBounds={[
                [-90, -180],
                [90, 180],
              ]}
              maxBoundsViscosity={1.0}
              className="h-full w-full"
            >
              <TileLayer noWrap={true} url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {radius && (
                <Circle
                  center={[lat, lng]}
                  radius={radius}
                  pathOptions={{
                    fillColor: '#621C43',
                    fillOpacity: 0.2,
                    color: '#621C43',
                    weight: 2,
                  }}
                />
              )}
              <Marker position={[lat, lng]}>
                <Popup>
                  {siteData.details?.find((d: Detail) => d.id === 'siteName')?.value || 'Unknown'}
                  {' - '}
                  {siteData.details?.find((d: Detail) => d.id === 'country')?.value || 'Unknown'}
                  <br />
                  Longitude - {Math.abs(lng).toFixed(4)}° {lng >= 0 ? 'E' : 'W'} and Latitude -{' '}
                  {Math.abs(lat).toFixed(4)}° {lat >= 0 ? 'N' : 'S'}
                  {radius && (
                    <>
                      <br />
                      Radius: {radius}m
                    </>
                  )}
                </Popup>
              </Marker>
            </MapContainer>

            {!isEditing && (
              <>
                <button
                  onClick={() => navigate(-1)}
                  className="absolute bottom-2 right-16 p-3 bg-white rounded-md shadow-md hover:bg-gray-100 transition duration-150 text-gray z-[1000] flex items-center"
                  title="Go Back"
                >
                  <ArrowLeft className="w-4 h-5 mr-2" />
                </button>

                <button
                  onClick={() => setIsEditing(true)}
                  className="absolute bottom-2 right-2 p-3 bg-white rounded-md shadow-md hover:bg-gray-100 transition duration-150 text-gray z-[1000]"
                  title="Edit Fields"
                >
                  <Edit className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProjectSite;
